import React from 'react'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import { EditorColoreApiType, EditorErrorType, EditorImageType } from '@utils/types'
import { FormToggle, EditorColor, EditorImage } from '@components/atoms'
import { Eye, EyeSlash } from '@components/icons'
import EditorSidebarElement from '../EditorSidebarElement/EditorSidebarElement'
import styles from './EditorSidebarImage.module.sass'

interface Props {
  image?: EditorImageType
  selected?: boolean
  onRemove?: () => void
  onColorChange?: (id: number, o: string, n: string) => void
  onColorVisibilityChange?: (id: number, colorId: string, visible: boolean) => void
  onMonochromaticSwitch?: (id: number, v: boolean) => void
  onNoBackgroundSwitch?: (id: number, v: boolean) => void
  error?: EditorErrorType
  colorLimits?: {
    allowedColors?: EditorColoreApiType[]
    lockColorChange?: EditorColoreApiType | null
    isGlobalColorMode?: boolean
  }
  className?: string
}

const EditorSidebarImage = (props: Props) => {
  const {
    image,
    selected = false,
    onRemove,
    onColorChange,
    onColorVisibilityChange,
    onMonochromaticSwitch,
    onNoBackgroundSwitch,
    error = undefined,
    colorLimits,
    className = '',
  } = props

  const t = useTrans()

  return image ? (
    <EditorSidebarElement
      selected={selected}
      variant={error ? error.variant : undefined}
      className={cn(styles.root, className)}
      onClose={onRemove}
    >
      <EditorImage image={image} error={error} onClose={onRemove} className={styles.image} />
      {image?.isVector && (
        <div className={styles.fields}>
          {image.hasBackground && (
            <FormToggle
              id={`editorsidebarimage_${image.id}_removebg`}
              label={t('Rimuovi background')}
              checked={image.noBackground}
              onChange={(e) => {
                onNoBackgroundSwitch(image.id, e.target.checked)
              }}
            />
          )}
          {image?.colori?.length > 1 &&
            !colorLimits?.isGlobalColorMode &&
            !colorLimits?.lockColorChange && (
              <FormToggle
                id={`editorsidebarimage_${image.id}_monochrome`}
                label={t('Monocromatico')}
                checked={image.monochrome}
                onChange={(e) => {
                  onMonochromaticSwitch(image.id, e.target.checked)
                }}
              />
            )}
          {!!image?.colori?.length && (
            <div className={styles.colors}>
              {image.colori.map((c) => {
                const isHidden = !!c.hidden
                const activeColorsCount = image.colori.filter((color) => !color.hidden).length
                const canHideColor = !isHidden && activeColorsCount > 1
                const visibilityLabel = isHidden ? t('Mostra colore') : t('Nascondi colore')

                return (
                  <div
                    key={c.id}
                    className={cn(styles.colorRow, isHidden ? styles['colorRow--hidden'] : '')}
                  >
                    <EditorColor
                      id={`editorsidebarimage_${image.id}_color_${c.id}`}
                      variant={c.variant}
                      value={c.value}
                      onChange={(colorValue) => {
                        onColorChange &&
                          onColorChange(image.id, c.originalValue || c.id || c.value, colorValue)
                      }}
                      allowedColors={colorLimits?.allowedColors}
                      lockColorChange={colorLimits?.lockColorChange}
                      className={styles.colorField}
                    />
                    <button
                      type="button"
                      className={styles.visibilityButton}
                      aria-label={visibilityLabel}
                      title={visibilityLabel}
                      disabled={!canHideColor && !isHidden}
                      onClick={() => {
                        onColorVisibilityChange?.(
                          image.id,
                          c.originalValue || c.id || c.value,
                          isHidden
                        )
                      }}
                    >
                      {isHidden ? <EyeSlash /> : <Eye />}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </EditorSidebarElement>
  ) : (
    <></>
  )
}

export default EditorSidebarImage
